import type { Locale } from "./i18n/locales";
import mandarin from "../messages/ai-continuation-zh-Hans.json";

export type AiContinuationPageType =
  | "notes-article"
  | "notes-hub"
  | "resource-directory"
  | "practical"
  | "tuition"
  | "scholarship";

export type AiContinuationPromptInput = {
  locale?: Locale;
  pageType: AiContinuationPageType;
  title: string;
  url: string;
  subject?: string | null;
  description?: string | null;
};

type PagePromptConfig = {
  readerTask: string;
  nextSteps: readonly string[];
};

function joinPromptParagraphs(paragraphs: readonly string[]): string {
  return paragraphs.join("\n\n");
}

const PAGE_PROMPT_CONFIG: Record<AiContinuationPageType, PagePromptConfig> = {
  "notes-article": {
    readerTask:
      "Help me understand this study guide, find weak spots, and turn the ideas into practice questions.",
    nextSteps: [
      "Start with the main idea in simple words.",
      "Ask me which part I am stuck on before giving a long solution.",
      "Make a short practice set and explain the first question step by step.",
    ],
  },
  "notes-hub": {
    readerTask:
      "Help me choose where to start on this notes hub and plan a realistic revision path.",
    nextSteps: [
      "Map the main topic areas on the page.",
      "Ask for my level, next test date, and weakest topics.",
      "Recommend the most relevant topic card and whether I should open its Notes, MCQ, or Video first, then suggest what to do next.",
    ],
  },
  "resource-directory": {
    readerTask:
      "Help me choose the most useful Eclat resource for my level, subject, and current goal.",
    nextSteps: [
      "Ask for my current level and subject before recommending a route.",
      "Help me decide whether I need notes, practical preparation, tuition, or a planning guide.",
      "Use links from the directory when they fit, and explain why each suggestion is relevant.",
    ],
  },
  practical: {
    readerTask:
      "Help me understand the practical or experiment page and what skills I should practise.",
    nextSteps: [
      "Explain the apparatus, data handling, graphing, planning, and sources of error mentioned on the page.",
      "Separate official page details from general practical advice.",
      "Ask what paper, school level, and experiment type I am preparing for before giving high-stakes advice.",
    ],
  },
  tuition: {
    readerTask:
      "Help me compare my needs against this tuition page and decide what questions to ask next.",
    nextSteps: [
      "Identify whether my issue is foundation, exam technique, motivation, workload, or class fit.",
      "Give me questions to ask Eclat before contacting them.",
      "Keep the advice practical and do not push me to enquire unless it fits my situation.",
    ],
  },
  scholarship: {
    readerTask:
      "Help me use this scholarship or study-abroad page to clarify fit and prepare my next questions.",
    nextSteps: [
      "Summarise what the page says about fit, deadlines, documents, and evidence to gather.",
      "Ask for my citizenship, level, grades, interests, and timeline before giving eligibility advice.",
      "Do not invent eligibility rules, fees, dates, or policy details.",
    ],
  },
};

export function buildAiContinuationPrompt({
  locale = "en",
  pageType,
  title,
  url,
  subject,
  description,
}: AiContinuationPromptInput): string {
  if (locale === "zh-Hans") {
    const config = mandarin.pages[pageType];
    return joinPromptParagraphs([
      `${mandarin.reference}${title}`,
      url,
      ...(subject ? [`${mandarin.subject}${subject}`] : []),
      ...(description ? [`${mandarin.summary}${description}`] : []),
      config.readerTask,
      [
        mandarin.todo,
        ...config.nextSteps.map((step, index) => `${index + 1}. ${step}`),
      ].join("\n"),
      [
        mandarin.boundaryHeading,
        ...mandarin.boundaries.map((step, index) => `${index + 1}. ${step}`),
      ].join("\n"),
      mandarin.question,
    ]);
  }
  const config = PAGE_PROMPT_CONFIG[pageType];
  const referenceLines = [
    `Use this Eclat page as the starting reference: ${title}`,
    url,
    subject ? `Subject or route: ${subject}` : null,
    description ? `Page summary: ${description}` : null,
  ].filter((line): line is string => line != null);

  return joinPromptParagraphs([
    ...referenceLines,
    config.readerTask,
    [
      "Please do the following:",
      ...config.nextSteps.map((step, index) => `${index + 1}. ${step}`),
    ].join("\n"),
    [
      "Important boundaries:",
      "1. Separate what the page says from your own assumptions.",
      "2. Ask for missing details before giving high-stakes advice.",
      "3. Do not make unsupported claims about SEAB, MOE, Cambridge, IBO, school policies, fees, schedules, deadlines, or admissions rules.",
      "4. Turn my situation into concrete next steps.",
    ].join("\n"),
    "My question:",
  ]);
}
